import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import RecapItem from 'components/Content/Recap/RecapItem';
import { Grid, Button, Box } from 'components/ui';
import RecapImage from 'components/Content/Recap/RecapImage';
import Markdown from 'components/Markdown/Markdown';
import RichText from 'components/Content/RichText';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import resourceWrapper from 'utils/framework/resourceWrapper';
import RecapBasketEN from 'components/Content/Recap/RecapBasket/locales/RecapBasket_en_US';
import RecapBasketFR from 'components/Content/Recap/RecapBasket/locales/RecapBasket_fr_CA';
import {
    radii, colors, fontSizes, space, lineHeights
} from 'style/config';
import uiUtils from 'utils/UI';
import RtcoOfferDetailsModal from 'components/Content/Recap/RecapBasket/RtcoOfferDetailsModal';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = resourceWrapper(getLocalisedDataFromResource(RecapBasketEN, RecapBasketFR));

const IMAGE_SIZE = [59, 48];

const PROMO_ARROW_SUFFIX = '\u00A0\u00A0\u23F5';

function appendPromoArrowToRichText(text) {
    if (!text?.json?.content) {
        return text;
    }

    const { content } = text.json;
    const lastParagraphIndex = content.findLastIndex(node => node.nodeType === 'paragraph');

    if (lastParagraphIndex === -1) {
        return text;
    }

    const lastParagraph = content[lastParagraphIndex];
    const updatedParagraph = {
        ...lastParagraph,
        content: [
            ...(Array.isArray(lastParagraph.content) ? lastParagraph.content : []),
            {
                data: {},
                marks: [],
                value: PROMO_ARROW_SUFFIX,
                nodeType: 'text'
            }
        ]
    };

    return {
        ...text,
        json: {
            ...text.json,
            content: content.map((node, index) => (index === lastParagraphIndex ? updatedParagraph : node))
        }
    };
}

function RecapBasket({ uniqueSkus, totalItemCount, shouldRenderCarouselBasket, ...props }) {
    const [isOfferDetailsModalOpen, setIsOfferDetailsModalOpen] = useState(false);

    let checkoutMessage = null;

    if (props.hasStandardItems) {
        if (props.hasSufficientPoints) {
            checkoutMessage = getText('applyPoints');
        } else if (!props.isBopisOnly) {
            checkoutMessage = getText('freeShipping');
        }
    }

    const { rtcoPromoData, rtcoPromoLoading, onPromoClick } = props;

    // Determine if promo should be shown
    const isControlGroup = rtcoPromoData?.isControl && rtcoPromoData?.isAbTest;
    const shouldShowPromo = rtcoPromoData && !isControlGroup && rtcoPromoData.isRtcoEnabled;

    const handlePromoClick = event => {
        event.stopPropagation();
        onPromoClick?.(rtcoPromoData);
        setIsOfferDetailsModalOpen(true);
    };

    const renderPromo = () => {
        if (rtcoPromoLoading && !rtcoPromoData) {
            return (
                <Box
                    css={styles.promoPlaceholder}
                    height={40}
                    borderRadius={radii[2]}
                    data-testid='skeleton'
                    aria-busy='true'
                />
            );
        }

        if (shouldShowPromo) {
            const { banner } = rtcoPromoData;
            const text = banner?.text ? appendPromoArrowToRichText(banner.text) : null;
            const hasCmsTheme = Boolean(banner?.backgroundColor || banner?.color);
            // When CMS supplies colors, preserve them on hover so the theme isn't lost.
            // Without a CMS theme, fall back to the default hardcoded hover in styles.promoContainer.
            const promoThemeCss = hasCmsTheme
                ? {
                    ...(banner?.backgroundColor && { backgroundColor: banner.backgroundColor }),
                    ...(banner?.color && { color: banner.color }),
                    '&:hover': {
                        ...(banner?.backgroundColor && { backgroundColor: banner.backgroundColor }),
                        ...(banner?.color && { color: banner.color })
                    }
                }
                : null;

            return (
                <Button
                    variant='secondary'
                    css={[styles.promoContainer, promoThemeCss]}
                    onClick={handlePromoClick}
                >
                    {text && (
                        <RichText
                            content={text}
                            style={styles.promoText}
                        />
                    )}
                </Button>
            );
        }

        return null;
    };

    return (
        <Fragment>
            <RecapItem
                {...props}
                // out of many items, only 1 will be rtco and only 1 will have this key
                isRtcoItem={Boolean('rtcoPromoData' in props)}
                wrapperIs='div'
            >
                {shouldRenderCarouselBasket && (
                    <Fragment>
                        {renderPromo()}
                        {props.showApplyPointsInReadyToCheckoutSection && checkoutMessage && (
                            <Markdown
                                css={styles.pill}
                                content={checkoutMessage}
                            />
                        )}
                        <Grid
                            width={IMAGE_SIZE}
                            repeat='fill'
                            gap={[1, 2]}
                            css={styles.productGrid}
                        >
                            {uniqueSkus.slice(0, 3).map((sku, index) => (
                                <RecapImage
                                    key={`recap_basket_${sku.skuId}`}
                                    sku={sku}
                                    size={IMAGE_SIZE}
                                    {...(index > 1 && {
                                        display: ['none', 'block']
                                    })}
                                />
                            ))}
                        </Grid>
                        <Button
                            is='span'
                            variant='special'
                            size='sm'
                            css={styles.checkoutButton}
                            children={getText('checkoutWithCount', false, String(totalItemCount))}
                        />
                    </Fragment>
                )}
            </RecapItem>
            {rtcoPromoData && (
                <RtcoOfferDetailsModal
                    isOpen={isOfferDetailsModalOpen && Boolean(shouldShowPromo && rtcoPromoData)}
                    onDismiss={() => setIsOfferDetailsModalOpen(false)}
                    rtcoPromoData={rtcoPromoData}
                />
            )}
        </Fragment>
    );
}

const styles = {
    productGrid: {
        marginTop: space[4],
        marginBottom: space[4]
    },
    checkoutButton: {
        marginTop: 'auto',
        marginBottom: space[1]
    },
    pill: {
        display: 'inline-block',
        padding: space[1],
        marginRight: 'auto',
        marginBottom: space[3],
        borderRadius: radii[2],
        backgroundColor: colors.lightBlue,
        fontSize: fontSizes.sm
    },
    promoPlaceholder: {
        width: '100%',
        marginBottom: space[3],
        ...uiUtils.SKELETON_ANIMATION
    },
    promoContainer: {
        height: 'auto',
        minHeight: 'auto',
        padding: space[1],
        marginBottom: space[1],
        borderRadius: radii[2],
        backgroundColor: colors.lightBlue,
        fontWeight: 'var(--font-weight-normal)',
        cursor: 'pointer',
        border: 'none',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: colors.blue,
            color: colors.white
        }
    },
    promoText: {
        alignSelf: 'start',
        fontSize: fontSizes.xs,
        lineHeight: lineHeights.tight,
        textAlign: 'start'
    }
};

RecapBasket.propTypes = {
    uniqueSkus: PropTypes.array.isRequired,
    totalItemCount: PropTypes.number.isRequired,
    shouldRenderCarouselBasket: PropTypes.bool.isRequired,
    rtcoPromoData: PropTypes.shape({
        context: PropTypes.string,
        variation: PropTypes.string,
        promoType: PropTypes.string,
        promoCode: PropTypes.string,
        isControl: PropTypes.bool,
        isDefault: PropTypes.bool,
        isAbTest: PropTypes.bool,
        isRtcoEnabled: PropTypes.bool,
        isRouge: PropTypes.bool,
        banner: PropTypes.shape({
            text: PropTypes.object,
            sid: PropTypes.string,
            mediaPlacement: PropTypes.string
        }),
        legalText: PropTypes.string,
        disclaimer: PropTypes.shape({
            type: PropTypes.string,
            sid: PropTypes.string,
            content: PropTypes.object
        }),
        modalSubtitle: PropTypes.string,
        modalEligibility: PropTypes.string
    }),
    rtcoPromoLoading: PropTypes.bool,
    onPromoClick: PropTypes.func
};

export default wrapFunctionalComponent(RecapBasket, 'RecapBasket');
